import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// Helper to detect schema errors
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const financialService = {
  async getRevenueTrends(days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select('fecha_ingreso, total')
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate?.toISOString())
        ?.order('fecha_ingreso', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching revenue trends:', error?.message);
        return [];
      }

      // Group by date and sum totals
      const revenueMap = {};
      data?.forEach(order => {
        const date = new Date(order?.fecha_ingreso)?.toISOString()?.split('T')?.[0];
        if (!revenueMap?.[date]) {
          revenueMap[date] = 0;
        }
        revenueMap[date] += parseFloat(order?.total || 0);
      });

      // Convert to array format for chart
      const revenueData = Object.keys(revenueMap)?.map(date => ({
        date,
        revenue: revenueMap?.[date]
      }));

      return revenueData;
    } catch (error) {
      console.error('Failed to fetch revenue trends:', error);
      throw error;
    }
  },

  async getOutstandingInvoices() {
    try {
      const { data, error } = await supabase
        ?.from('invoices')
        ?.select(`
          id,
          invoice_number,
          client_name,
          total,
          status,
          generated_at
        `)
        ?.in('status', ['generated', 'sent'])
        ?.order('generated_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching outstanding invoices:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch outstanding invoices:', error);
      throw error;
    }
  },

  async getPaymentRate() {
    try {
      // Get total invoices count
      const { count: totalCount, error: totalError } = await supabase
        ?.from('invoices')
        ?.select('*', { count: 'exact', head: true });

      if (totalError) {
        if (isSchemaError(totalError)) {
          console.error('Schema error:', totalError?.message);
          throw totalError;
        }
        console.log('Error counting total invoices:', totalError?.message);
        return 0;
      }

      // Get paid invoices count
      const { count: paidCount, error: paidError } = await supabase
        ?.from('invoices')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('status', 'paid');

      if (paidError) {
        if (isSchemaError(paidError)) {
          console.error('Schema error:', paidError?.message);
          throw paidError;
        }
        console.log('Error counting paid invoices:', paidError?.message);
        return 0;
      }

      if (totalCount === 0) return 0;

      const paymentRate = (paidCount / totalCount) * 100;
      return paymentRate;
    } catch (error) {
      console.error('Failed to calculate payment rate:', error);
      throw error;
    }
  },

  async getAverageCollectionTime() {
    try {
      const { data, error } = await supabase
        ?.from('invoices')
        ?.select('generated_at, updated_at')
        ?.eq('status', 'paid');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return 0;
        console.log('Error fetching paid invoices:', error?.message);
        return 0;
      }

      if (!data || data?.length === 0) return 0;

      // Calculate average days between generated_at and updated_at (payment date)
      const totalDays = data?.reduce((sum, invoice) => {
        const generatedDate = new Date(invoice?.generated_at);
        const paidDate = new Date(invoice?.updated_at);
        const diffTime = Math.abs(paidDate - generatedDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return sum + diffDays;
      }, 0);

      const avgCollectionTime = totalDays / data?.length;
      return avgCollectionTime;
    } catch (error) {
      console.error('Failed to calculate average collection time:', error);
      throw error;
    }
  },

  async getAllFinancialMetrics() {
    try {
      const [revenueTrends, outstandingInvoices, paymentRate, avgCollectionTime] = await Promise.all([
        this.getRevenueTrends(),
        this.getOutstandingInvoices(),
        this.getPaymentRate(),
        this.getAverageCollectionTime()
      ]);

      return {
        revenueTrends,
        outstandingInvoices,
        paymentRate,
        avgCollectionTime
      };
    } catch (error) {
      console.error('Failed to fetch all financial metrics:', error);
      throw error;
    }
  },

  async getPartsCostBreakdown(days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          numero_ot,
          fecha_ingreso,
          items_cotizacion
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate?.toISOString())
        ?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return { totalPartsCost: 0, breakdown: [], byCategory: [] };
        console.log('Error fetching parts cost breakdown:', error?.message);
        return { totalPartsCost: 0, breakdown: [], byCategory: [] };
      }

      let totalPartsCost = 0;
      const breakdown = [];
      const categoryMap = {};

      data?.forEach(order => {
        const items = order?.items_cotizacion || [];
        items?.forEach(item => {
          if (item?.tipo === 'refaccion') {
            const cost = parseFloat(item?.precio || 0) * parseFloat(item?.cantidad || 0);
            totalPartsCost += cost;
            
            breakdown?.push({
              workOrderId: order?.id,
              workOrderNumber: order?.numero_ot,
              date: order?.fecha_ingreso,
              partName: item?.descripcion,
              quantity: item?.cantidad,
              unitPrice: item?.precio,
              totalCost: cost
            });

            // Group by category (using first word of description as category)
            const category = item?.descripcion?.split(' ')?.[0] || 'Otros';
            if (!categoryMap?.[category]) {
              categoryMap[category] = 0;
            }
            categoryMap[category] += cost;
          }
        });
      });

      const byCategory = Object.keys(categoryMap)?.map(category => ({
        category,
        cost: categoryMap?.[category]
      }))?.sort((a, b) => b?.cost - a?.cost);

      return {
        totalPartsCost,
        breakdown,
        byCategory
      };
    } catch (error) {
      console.error('Failed to fetch parts cost breakdown:', error);
      throw error;
    }
  },

  async getLaborCostTracking(days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          numero_ot,
          fecha_ingreso,
          items_cotizacion,
          tecnico_asignado
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate?.toISOString())
        ?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return { totalLaborCost: 0, breakdown: [], byTechnician: [] };
        console.log('Error fetching labor cost tracking:', error?.message);
        return { totalLaborCost: 0, breakdown: [], byTechnician: [] };
      }

      let totalLaborCost = 0;
      const breakdown = [];
      const technicianMap = {};

      data?.forEach(order => {
        const items = order?.items_cotizacion || [];
        items?.forEach(item => {
          if (item?.tipo === 'servicio') {
            const cost = parseFloat(item?.precio || 0) * parseFloat(item?.cantidad || 0);
            totalLaborCost += cost;
            
            breakdown?.push({
              workOrderId: order?.id,
              workOrderNumber: order?.numero_ot,
              date: order?.fecha_ingreso,
              serviceName: item?.descripcion,
              quantity: item?.cantidad,
              unitPrice: item?.precio,
              totalCost: cost,
              technician: order?.tecnico_asignado || 'No asignado'
            });

            // Group by technician
            const technician = order?.tecnico_asignado || 'No asignado';
            if (!technicianMap?.[technician]) {
              technicianMap[technician] = 0;
            }
            technicianMap[technician] += cost;
          }
        });
      });

      const byTechnician = Object.keys(technicianMap)?.map(technician => ({
        technician,
        cost: technicianMap?.[technician]
      }))?.sort((a, b) => b?.cost - a?.cost);

      return {
        totalLaborCost,
        breakdown,
        byTechnician
      };
    } catch (error) {
      console.error('Failed to fetch labor cost tracking:', error);
      throw error;
    }
  },

  async getProfitMarginAnalysis(days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          numero_ot,
          fecha_ingreso,
          items_cotizacion,
          total
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate?.toISOString())
        ?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return { totalRevenue: 0, totalCost: 0, totalProfit: 0, profitMargin: 0, breakdown: [] };
        console.log('Error fetching profit margin analysis:', error?.message);
        return { totalRevenue: 0, totalCost: 0, totalProfit: 0, profitMargin: 0, breakdown: [] };
      }

      let totalRevenue = 0;
      let totalCost = 0;
      const breakdown = [];

      data?.forEach(order => {
        const revenue = parseFloat(order?.total || 0);
        const items = order?.items_cotizacion || [];
        
        // Calculate cost (assuming 70% of price for parts and 60% for services as cost)
        let orderCost = 0;
        items?.forEach(item => {
          const itemTotal = parseFloat(item?.precio || 0) * parseFloat(item?.cantidad || 0);
          if (item?.tipo === 'refaccion') {
            orderCost += itemTotal * 0.7; // 70% cost for parts
          } else if (item?.tipo === 'servicio') {
            orderCost += itemTotal * 0.6; // 60% cost for labor
          }
        });

        const profit = revenue - orderCost;
        const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

        totalRevenue += revenue;
        totalCost += orderCost;

        breakdown?.push({
          workOrderId: order?.id,
          workOrderNumber: order?.numero_ot,
          date: order?.fecha_ingreso,
          revenue,
          cost: orderCost,
          profit,
          margin
        });
      });

      const totalProfit = totalRevenue - totalCost;
      const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

      return {
        totalRevenue,
        totalCost,
        totalProfit,
        profitMargin,
        breakdown
      };
    } catch (error) {
      console.error('Failed to fetch profit margin analysis:', error);
      throw error;
    }
  }
};